import * as React from 'react';
import * as Images from 'pos/Images';

import { Permission, PointOfSaleStatusDTO, Res } from 'core/api';
import { SessionProvider } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import { RestaurantFacade } from 'pos/facades';
import { Col, Cols, Flex, Format } from 'core/ui';
import { PrintBalanceNextAction } from 'pos/modules/helpers/PrintBalanceNextAction';
import { Message, Modal } from 'core/ui-next/modal';

type Props = {
	initialStatuses: PointOfSaleStatusDTO[]
} & Modal.Actions<PrintBalanceNextAction>;

type State = {
	statuses: PointOfSaleStatusDTO[];
	selectedStatus: Nullable<PointOfSaleStatusDTO>;
}

class ShowPointsOfSaleStatus extends PosScreenClassComponent<Props, State> {
	createInitialState(props: Props): Nullable<State> {
		return {
			selectedStatus: null,
			statuses: props.initialStatuses
		};
	}

	protected getScreenId(): string {
		return 'SHOW_POINT_OF_SALE_STATUS';
	}

	protected getTitle(): string {
		return Res.Web.SHOW_POINT_OF_SALES_STATUS();
	}

	protected getImage(): string | SvgIcon {
		return Images.PointOfSaleStatus;
	}

	protected getSize(): ScreenSize {
		return 'small';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		const closeAllButton: Nullable<ActionButtonDefinition> = this.state.statuses.filter(s => !s.IsClosed && s.Id !== SessionProvider.getCurrent().PosId).length > 0
			? { title: Res.Web.SHOW_POINT_OF_SALES_STATUS_CLOSE_ALL_BTN(), image: Images.ForceCloseDayAll, action: this.closeAllPos }
			: null;

		const closeDayButton: Nullable<ActionButtonDefinition> = this.state.selectedStatus !== null && this.state.selectedStatus.Id !== SessionProvider.getCurrent().PosId && !this.state.selectedStatus.IsClosed
			? { title: Res.Web.SHOW_POINT_OF_SALES_STATUS_CLOSE_POS_BTN(), image: Images.ForceCloseDay, action: this.closePos }
			: null;

		return [
			this.cancelButton(),
			null,
			null,
			null,
			closeDayButton,
			closeAllButton,
		];
	}

	protected useKeypad(): boolean {
		return false;
	}

	onSelectStatus = (status: PointOfSaleStatusDTO) => {
		this.setState({ selectedStatus: status });
	};

	closeAllPos = async (): Promise<void> => {
		if (SessionProvider.isSlave()) {
			await Message.show(Res.Msg.MSG_OPERATION_ONLY_VALID_IN_MASTER_POS());
			return;
		}

		const ids = (await RestaurantFacade.getPointsOfSaleStatus())
			.filter(s => s.Id !== SessionProvider.getCurrent().PosId && !s.IsClosed)
			.map(s => s.Id);

		const closed = await forceClose(ids);
		if (!closed)
			return;

		const result = await Message.confirm(Res.Msg.MSG_CONFIRM_CLOSE_DAY());
		if (!result.ok)
			await this.reloadStatus();
		else
			this.close(PrintBalanceNextAction.CloseDay);

	};

	closePos = async (): Promise<void> => {
		if (SessionProvider.isSlave()) {
			await Message.show(Res.Msg.MSG_OPERATION_ONLY_VALID_IN_MASTER_POS());
			return;
		}

		if (this.state.selectedStatus !== null) {

			const closed = await forceClose([this.state.selectedStatus.Id]);
			if (closed)
				await this.reloadStatus();
		}
	};

	reloadStatus = async (): Promise<void> => {
		const statuses = await RestaurantFacade.getPointsOfSaleStatus();
		const selectedStatus = statuses.find(x => x.Id === this.state.selectedStatus?.Id) || null;
		this.setState({ statuses, selectedStatus });
	};

	protected getContent(): React.ReactNode {
		const statusesList = this.state.statuses.map(s => {
			const selectedStatusClass = this.state.selectedStatus && s.Id === this.state.selectedStatus.Id ? 'selected' : '';
			return <li key={s.Id} onClick={() => this.onSelectStatus(s)} className={selectedStatusClass}>
				<Cols>
					<Col className='text-left' width='*' >
						{s.Name}
					</Col>
					<Col className='text-left' width='*'>
						{s.OpenDate !== null ? Format.asDateAndTime(s.OpenDate) : Res.Web.POS_STATUS_CONTROL_CLOSED_LBL()}
					</Col>
					<Col className='text-right' width='*'>
						{Format.asMoney(s.CurrentAmount)}
					</Col>
				</Cols>
			</li>;
		});

		return <Flex.Vertical>
			<Flex.Item>
				<p className='p-t-sm p-r-sm p-l-sm'>{Res.Web.SHOW_POINT_OF_SALES_STATUS_INFO_LBL()}</p>
			</Flex.Item>
			<Flex.Item>
				<Cols className='list-header'>
					<Col width='*'>
						{Res.Web.POS_STATUS_CONTROL_POS_CLM()}
					</Col>
					<Col width='*'>
						{Res.Web.POS_STATUS_CONTROL_OPEN_DATE_CLM()}
					</Col>
					<Col width='*'>
						{Res.Web.POS_STATUS_CONTROL_CURRENT_AMOUNT_CLM()}
					</Col>
				</Cols>
			</Flex.Item>
			<Flex.Item size='*'>
				<ul className='list'>
					{statusesList}
				</ul>
			</Flex.Item>
		</Flex.Vertical>;
	}

	onComponentDidMount(): void { }
	onComponentWillUnmount(): void { }
}

async function forceClose(posIds: number[]): Promise<boolean> {

	if (posIds.some(id => id === SessionProvider.getCurrent().PosId)) {
		await Message.show(Res.Msg.MSG_CANNOT_CLOSE_DAY_ON_CURRENT_POS());
		return false;
	}

	const confirmResult = await Message.confirm(Res.Msg.MSG_CONFIRM_CLOSE_DAY_ON_OTHER_POS());
	if (!confirmResult.ok)
		return false;

	await RestaurantFacade.foreCloseDay(posIds);
	return true;
}

declare global {
	interface CommandType {
		showPointsOfSaleStatus: () => Promise<Result<PrintBalanceNextAction>>
	}
}

Command.register('showPointsOfSaleStatus', Preconditions.ensure(Permission.System_ShowPointsOfSaleStatus), async (): Promise<Result<PrintBalanceNextAction>> => {
	const statuses = await RestaurantFacade.getPointsOfSaleStatus();
	return await Modal.show(ShowPointsOfSaleStatus, { initialStatuses: statuses });
});